import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Ban, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { name: "", contact_person: "", email: "", phone: "", city: "", address: "", supplier_code: "", gst_number: "" };

export default function AdminSuppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/suppliers").then((r) => setSuppliers(r.data.suppliers)).catch(() => toast.error("Failed to load suppliers"));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/suppliers/${editing.id}`, form);
        toast.success("Supplier updated");
      } else {
        await api.post("/suppliers", { ...form, create_login: false });
        toast.success("Supplier added");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const toggleStatus = async (s) => {
    try {
      await api.post(`/suppliers/${s.id}/${s.status === "inactive" ? "activate" : "deactivate"}`);
      toast.success(s.status === "inactive" ? "Supplier activated" : "Supplier deactivated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-suppliers-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Supplier Management</h1>
          <p className="text-sm text-muted-foreground">{suppliers.length} suppliers · codes, contacts and performance statistics</p>
        </div>
        <Button data-testid="add-supplier-btn" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800"><Plus className="mr-1.5 h-4 w-4" /> Add Supplier</Button>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full text-sm" data-testid="admin-suppliers-table">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3 font-medium">Code</th>
              <th className="px-4 py-3 font-medium">Company</th>
              <th className="px-4 py-3 font-medium">Contact</th>
              <th className="px-4 py-3 font-medium">WhatsApp</th>
              <th className="px-4 py-3 font-medium text-right">Total</th>
              <th className="px-4 py-3 font-medium text-right">Active</th>
              <th className="px-4 py-3 font-medium text-right">Delivered</th>
              <th className="px-4 py-3 font-medium text-right">Delayed</th>
              <th className="px-4 py-3 font-medium text-right">On-Time</th>
              <th className="px-4 py-3 font-medium text-right">Avg Resp</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map((s) => (
              <tr key={s.id} data-testid={`admin-supplier-${s.name.replace(/\W+/g, "-").toLowerCase()}`} className="border-b last:border-0 hover:bg-slate-50">
                <td className="px-4 py-3 font-mono text-xs">{s.supplier_code || "—"}</td>
                <td className="px-4 py-3"><p className="font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.address || s.city}</p></td>
                <td className="px-4 py-3"><p>{s.contact_person}</p><p className="text-xs text-muted-foreground">{s.email}</p></td>
                <td className="px-4 py-3 font-mono text-xs">{s.phone}</td>
                <td className="px-4 py-3 text-right font-mono">{s.metrics.total_pos}</td>
                <td className="px-4 py-3 text-right font-mono">{s.metrics.active_pos}</td>
                <td className="px-4 py-3 text-right font-mono">{s.metrics.completed_pos}</td>
                <td className={`px-4 py-3 text-right font-mono ${s.metrics.delayed_pos > 0 ? "font-semibold text-[#DC2626]" : ""}`}>{s.metrics.delayed_pos}</td>
                <td className="px-4 py-3 text-right font-mono">{s.metrics.on_time_pct != null ? `${s.metrics.on_time_pct}%` : "—"}</td>
                <td className="px-4 py-3 text-right font-mono">{s.metrics.avg_response_hours != null ? `${s.metrics.avg_response_hours}h` : "—"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.status === "inactive" ? "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]" : "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]"}`}>
                    {(s.status || "active").toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" data-testid={`supplier-edit-${s.id.slice(0, 8)}`} onClick={() => { setEditing(s); setForm({ name: s.name, contact_person: s.contact_person, email: s.email, phone: s.phone, city: s.city || "", address: s.address || "", supplier_code: s.supplier_code || "", gst_number: s.gst_number || "" }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`supplier-toggle-${s.id.slice(0, 8)}`} onClick={() => toggleStatus(s)} className={s.status === "inactive" ? "text-green-600" : "text-red-500"}>
                      {s.status === "inactive" ? <CheckCircle2 className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">{editing ? `Edit ${editing.name}` : "Add Supplier"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Company Name *</Label><Input data-testid="supplier-form-name" required className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Supplier Code</Label><Input data-testid="supplier-form-code" className="mt-1.5 font-mono" value={form.supplier_code} onChange={(e) => setForm({ ...form, supplier_code: e.target.value })} placeholder="SUP-011" /></div>
              <div><Label>Contact Person *</Label><Input data-testid="supplier-form-contact" required className="mt-1.5" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
              <div><Label>City</Label><Input data-testid="supplier-form-city" className="mt-1.5" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label>Email *</Label><Input data-testid="supplier-form-email" type="email" required className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>WhatsApp Number *</Label><Input data-testid="supplier-form-phone" required className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>GST Number *</Label><Input data-testid="supplier-form-gst" required className="mt-1.5 font-mono" placeholder="27ABCDE1234F1Z5" maxLength={15} value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase() })} /></div>
            </div>
            <div><Label>Address</Label><Input data-testid="supplier-form-address" className="mt-1.5" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="supplier-form-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editing ? "Save Changes" : "Add Supplier"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
